import type { MultiLineString } from 'geojson';
import flattenDeep from 'lodash-es/flattenDeep';
import merge from 'lodash-es/merge';
import moment from 'moment-timezone';
import { createAppSlice } from '.';
import {
  addParcelsToDeviceSession as addParcelsToDeviceSessionApi,
  freezeSession,
  freezeSessions,
  getEquipmentSession,
  getEquipmentSessions,
  getParcelSession,
  ignoreParcelsToDeviceSession as ignoreParcelsToDeviceSessionApi,
  updateClusterIdForSession as updateClusterIdForSessionApi,
} from '../api/api';
import { sessionQueryKeys } from '../api/queries/keys/sessions';
import { TaskId } from '../api/queries/workOrders';
import { queryClient } from '../api/queryClient';
import { equipmentSession as equipmentSessionSchema, parcelSession } from '../api/schema';
import { EquipmentBillingMethod, EquipmentGpioMethod, EquipmentModel } from '../constants/equipmentConstants';
import { DeviceSessionStatus } from '../constants/reportConstants';
import { SNACK_CONSTANTS } from '../constants/snackConstants';
import { I18n, LocaleKey } from '../i18n';
import { normalize } from '../utils';
import { KeysToCamelCase } from '../utils/types';
import { ClusterId } from './clusters';
import { DeviceId, devicePositionUpdated } from './devices';
import { EquipmentId, EquipmentModelId } from './equipments';
import { ParcelId, fetchAnonymousParcelsByIds, selectParcelsById } from './parcels';
import { alerting, snackAdded } from './snacks';
import { userLoggedOut } from './users';

export type DeviceSessionId = string;
export type EquipmentSessionId = number;
export type ParcelSessionId = number;

export type ParcelSession = {
  id: ParcelSessionId;
  parcel_id: ParcelId;
  equipment_session_id: EquipmentSessionId;
  from_date: string;
  to_date: string;
  distance: number;
  motor_duration: number;
  movement_duration: number;
  area: number;
  unit: number;
  fuel: number | null;
  parcel_area: number;
  parcel_name: string;
  speed_min: number;
  speed_max: number;
  device_session_status: DeviceSessionStatus;
  task_ids?: TaskId[];
  equipment_model: EquipmentModel;
  equipment_gpio_default_unit_method: EquipmentGpioMethod;
  equipment_model_id: EquipmentModelId;
  equipment_instance_id: EquipmentId;
  equipment_name: string;
};

export type EquipmentSession = {
  id: EquipmentSessionId;
  device_instance_id: DeviceId;
  device_instance_name: string;
  beacon_name: string;
  from_date: string;
  to_date: string;
  cluster_id: ClusterId;
  motor_duration: number;
  movement_duration: number;
  area: number;
  unit: number;
  distance: number;
  fuel: number | null;
  parcel_sessions: ParcelSession[];
  device_session_uuid: DeviceSessionId;
  processing_status: string;
  device_session_status: DeviceSessionStatus;
  duration: number;
  in_parcel_motor_duration: number;
  in_farmhouse_motor_duration: number;
  out_parcel_motor_duration: number;
  in_parcel_duration: number;
  out_parcel_duration: number;
  in_parcel_distance: number;
  out_parcel_distance: number;
  in_parcel_fuel: number | null;
  out_parcel_fuel: number | null;
  has_can_data: boolean;
  task_ids?: TaskId[];
  equipment_instance_id: EquipmentId;
  equipment_instance_label: string;
  equipment_billing_method: EquipmentBillingMethod;
  equipment_model: EquipmentModel;
  equipment_gpio_default_unit_method: EquipmentGpioMethod;
  parent_device_session_id: DeviceSessionId;
  beacon_uuid?: string;
  track: string;
  share_counter?: number;
  share_expiration?: string;
  share_full?: boolean;
  share_id?: string;
};
// the API sends `track` as serialized geojson, parsed on the way into the store
export type NormalizedEquipmentSession = KeysToCamelCase<
  Omit<EquipmentSession, 'parcel_sessions' | 'track'> & {
    parcel_sessions: ParcelSessionId[];
    track?: MultiLineString;
  }
>;

export type NormalizedParcelSession = KeysToCamelCase<ParcelSession>;

type EquipmentSessionRow = { equipment_sessions: EquipmentSession[] };

type SessionEntities = {
  entities: {
    equipmentSession?: Record<EquipmentSessionId, NormalizedEquipmentSession>;
    parcelSession?: Record<ParcelSessionId, NormalizedParcelSession>;
  };
  result: EquipmentSessionId[];
};

type State = {
  parcelSessionById: Record<ParcelSessionId, NormalizedParcelSession>;
  parcelSessionWithTracksById: Record<ParcelSessionId, NormalizedParcelSession>;
  equipmentSessionById: Record<EquipmentSessionId, NormalizedEquipmentSession>;
  equipmentSessionsIdsByEquipmentId: Record<EquipmentId, EquipmentSessionId[]>;
};

const initialState: State = {
  parcelSessionById: {},
  parcelSessionWithTracksById: {},
  equipmentSessionById: {},
  equipmentSessionsIdsByEquipmentId: {},
};

type Dispatch = Parameters<typeof alerting>[1]['dispatch'];

async function alertingWith<T>(call: () => Promise<T>, messageKey: LocaleKey, dispatch: Dispatch) {
  try {
    return await call();
  } catch (e) {
    dispatch(
      snackAdded({ type: SNACK_CONSTANTS.TYPE.ERROR, duration: 5000, message: I18n.t(messageKey), action: 'OK' }),
    );
    throw e;
  }
}

// a session parcel missing from the store is necessarily an anonymous one
function fetchParcelsOfSession(
  session: EquipmentSession,
  dispatch: Dispatch,
  getState: () => unknown,
  signal: AbortSignal,
) {
  const parcelById = selectParcelsById(getState() as Parameters<typeof selectParcelsById>[0]);
  const missingParcelIds = [...new Set((session.parcel_sessions || []).map((ps) => ps.parcel_id))].filter(
    (id) => !parcelById[id],
  );
  if (missingParcelIds.length) {
    // plug the fetch abort to the signal
    const promise = dispatch(fetchAnonymousParcelsByIds(missingParcelIds));
    signal.addEventListener('abort', () => promise.abort());
  }
}

function invalidateForcedOrIgnoredParcels(deviceSessionId: DeviceSessionId) {
  void queryClient.invalidateQueries({ queryKey: sessionQueryKeys.forcedOrIgnoredParcels(deviceSessionId) });
}

const sessionsSlice = createAppSlice({
  name: 'sessions',
  initialState,
  reducers: (create) => ({
    fetchEquipmentSession: create.asyncThunk(
      async (id: EquipmentSessionId, { dispatch, getState, signal }) => {
        let session: EquipmentSession;
        try {
          session = (await getEquipmentSession(id, signal)) as EquipmentSession;
        } catch (e) {
          dispatch(
            snackAdded({
              type: SNACK_CONSTANTS.TYPE.ERROR,
              duration: 5000,
              message: I18n.t('EquipmentSession.session_not_exists'),
              action: 'OK',
            }),
          );
          throw e;
        }
        // an aborted `get` resolves with no payload, so reject rather than normalize `undefined`
        signal.throwIfAborted();
        fetchParcelsOfSession(session, dispatch, getState, signal);
        return normalize(session, equipmentSessionSchema) as SessionEntities;
      },
      {
        fulfilled: (state, { payload }) => {
          Object.assign(state.equipmentSessionById, payload.entities.equipmentSession);
          merge(state.parcelSessionById, payload.entities.parcelSession);
        },
      },
    ),
    fetchEquipmentSessions: create.asyncThunk(
      async (
        {
          id,
          start,
          end,
          params,
          withEveryActive = false,
        }: {
          id: EquipmentId;
          start: string;
          end: string;
          params?: string;
          withEveryActive?: boolean;
        },
        { dispatch, signal },
      ) => {
        const rows = (await alerting(
          () => getEquipmentSessions({ id, start, end, format: undefined, params, signal }),
          {
            dispatch,
          },
        )) as EquipmentSessionRow[] | undefined;
        const activeRows = withEveryActive
          ? ((await alerting(
              () =>
                getEquipmentSessions({
                  id,
                  start: moment().subtract(1, 'day').startOf('day').toISOString(),
                  end: moment().endOf('day').toISOString(),
                  format: undefined,
                  params: '&withRelated=true&status=active',
                  signal,
                }),
              { dispatch },
            )) as EquipmentSessionRow[] | undefined)
          : [];
        const sessions = (rows || [])
          .map((row) => row.equipment_sessions)
          .concat((activeRows || []).map((row) => row.equipment_sessions));
        return { id, data: normalize(flattenDeep(sessions), [equipmentSessionSchema]) as SessionEntities };
      },
      {
        fulfilled: (state, { payload }) => {
          Object.assign(state.equipmentSessionById, payload.data.entities.equipmentSession);
          Object.assign(state.parcelSessionById, payload.data.entities.parcelSession);
          state.equipmentSessionsIdsByEquipmentId[payload.id] = payload.data.result;
        },
      },
    ),
    fetchParcelSessionById: create.asyncThunk(
      async (id: ParcelSessionId, { dispatch }) => {
        const session = await alerting(() => getParcelSession(id), { dispatch });
        return normalize([session], [parcelSession]) as SessionEntities;
      },
      {
        fulfilled: (state, { payload }) => {
          Object.assign(state.equipmentSessionById, payload.entities.equipmentSession);
          Object.assign(state.parcelSessionWithTracksById, payload.entities.parcelSession);
        },
      },
    ),
    updateClusterIdForSession: create.asyncThunk(
      async (
        {
          sessionId,
          clusterId,
          withSnack = true,
        }: { sessionId: EquipmentSessionId; clusterId: ClusterId; withSnack?: boolean },
        { dispatch, getState, signal },
      ) => {
        const session = (await alerting(() => updateClusterIdForSessionApi(sessionId, clusterId), {
          dispatch,
        })) as EquipmentSession;
        if (withSnack) {
          dispatch(
            snackAdded({
              type: SNACK_CONSTANTS.TYPE.SUCCESS,
              duration: 3000,
              message: I18n.t('Session.updated_success'),
              action: 'OK',
            }),
          );
        }
        fetchParcelsOfSession(session, dispatch, getState, signal);
        return normalize(session, equipmentSessionSchema) as SessionEntities;
      },
      {
        fulfilled: (state, { payload }) => {
          Object.assign(state.equipmentSessionById, payload.entities.equipmentSession);
          merge(state.parcelSessionById, payload.entities.parcelSession);
        },
      },
    ),
    freezeEquipmentSession: create.asyncThunk(
      async ({ sessionId, status }: { sessionId: EquipmentSessionId; status: string }, { dispatch }) => {
        await alerting(() => freezeSession(sessionId, status), { dispatch });
        return { sessionId, status };
      },
      {
        fulfilled: (state, { payload }) => {
          const session = state.equipmentSessionById[payload.sessionId];
          if (session) {
            session.processingStatus = payload.status;
          }
        },
      },
    ),
    freezeEquipmentSessions: create.asyncThunk(
      async ({ sessionIds, status }: { sessionIds: EquipmentSessionId[]; status: string }, { dispatch }) => {
        await alerting(() => freezeSessions(sessionIds, status), { dispatch });
        return { sessionIds, status };
      },
      {
        fulfilled: (state, { payload }) => {
          payload.sessionIds.forEach((id) => {
            const session = state.equipmentSessionById[id];
            if (session) {
              session.processingStatus = payload.status;
            }
          });
        },
      },
    ),
    addParcelsToDeviceSession: create.asyncThunk(
      async (
        {
          deviceSessionId,
          parcelIds,
          disableResetSession,
        }: { deviceSessionId: DeviceSessionId; parcelIds: ParcelId[]; disableResetSession?: boolean },
        { dispatch },
      ) => {
        await alertingWith(
          () => addParcelsToDeviceSessionApi(deviceSessionId, parcelIds, disableResetSession),
          'EquipmentSession.error_to_add_parcels',
          dispatch,
        );
        invalidateForcedOrIgnoredParcels(deviceSessionId);
        dispatch(
          snackAdded({
            type: SNACK_CONSTANTS.TYPE.SUCCESS,
            duration: 3000,
            message: I18n.t('EditDeviceSessionParcels.success'),
            action: SNACK_CONSTANTS.ACTION.OK,
          }),
        );
      },
    ),
    ignoreParcelsToDeviceSession: create.asyncThunk(
      async (
        {
          deviceSessionId,
          parcelIds,
          disableResetSession = false,
          withSnack = true,
        }: {
          deviceSessionId: DeviceSessionId;
          parcelIds: ParcelId[];
          disableResetSession?: boolean;
          withSnack?: boolean;
        },
        { dispatch },
      ) => {
        await alertingWith(
          () => ignoreParcelsToDeviceSessionApi(deviceSessionId, parcelIds, disableResetSession),
          'EquipmentSession.error_to_ignore_parcels',
          dispatch,
        );
        invalidateForcedOrIgnoredParcels(deviceSessionId);
        if (withSnack) {
          dispatch(
            snackAdded({
              type: SNACK_CONSTANTS.TYPE.SUCCESS,
              duration: 3000,
              message: I18n.t('EditDeviceSessionParcels.success'),
              action: SNACK_CONSTANTS.ACTION.OK,
            }),
          );
        }
      },
    ),
    sharedSessionUpdated: create.reducer<{
      id: EquipmentSessionId;
      share_counter: number;
      share_expiration: string;
      share_full: boolean;
      share_id: string;
    }>((state, { payload }) => {
      const session = state.equipmentSessionById[payload.id];
      if (!session) {
        return;
      }
      session.shareCounter = payload.share_counter;
      session.shareExpiration = payload.share_expiration;
      session.shareFull = payload.share_full;
      session.shareId = payload.share_id;
    }),
  }),
  extraReducers: (builder) => {
    builder.addCase(devicePositionUpdated, (state, { payload }) => {
      Object.values(state.equipmentSessionById).forEach((session) => {
        if (session.deviceInstanceId !== payload.deviceId || !session.track?.coordinates) {
          return;
        }
        // the track is a MultiLineString: the new position continues its last line, or starts one when there is none
        const position = [payload.locationLongitude, payload.locationLatitude];
        const lastLine = session.track.coordinates.at(-1);
        if (lastLine) {
          lastLine.push(position);
        } else {
          session.track.coordinates.push([position]);
        }
      });
    });
    builder.addCase(userLoggedOut, () => initialState);
  },
  selectors: {
    selectEquipmentSessionById: (state) => state.equipmentSessionById,
    selectParcelSessionById: (state) => state.parcelSessionById,
    selectParcelSessionWithTracksById: (state) => state.parcelSessionWithTracksById,
    selectEquipmentSessionIds: (state, equipmentId: EquipmentId) =>
      state.equipmentSessionsIdsByEquipmentId[equipmentId] || [],
  },
});

export const {
  selectEquipmentSessionById,
  selectEquipmentSessionIds,
  selectParcelSessionById,
  selectParcelSessionWithTracksById,
} = sessionsSlice.selectors;

export const {
  fetchEquipmentSession,
  fetchEquipmentSessions,
  fetchParcelSessionById,
  updateClusterIdForSession,
  freezeEquipmentSession,
  freezeEquipmentSessions,
  addParcelsToDeviceSession,
  ignoreParcelsToDeviceSession,
  sharedSessionUpdated,
} = sessionsSlice.actions;

export default sessionsSlice.reducer;
